import React, { useState } from 'react'
import { compact, uniqBy } from 'lodash-es'
import { CloudUploadIcon } from 'lucide-react'
import { DropzoneOptions } from 'react-dropzone'

import { uploadFileWithProgress } from '@/lib/upload.ts'
import {
  FileInput,
  FileUploader,
  FileUploaderContent,
} from '@/components/ui/extension/file-upload.tsx'
import { UploaderItem } from '@/components/uploader-item.tsx'
import { apiClient, formatClientResponse } from '@/apiClient.ts'

type LocalFile = File

const dropZoneConfig: DropzoneOptions = {
  maxFiles: 5,
  maxSize: 1024 * 1024 * 50,
  multiple: true,
  // accept: { 'application/pdf': ['.pdf'] },
}

export type UploadingFile = {
  file: File
  fingerprint: string
  progress: number
  status: 'idle' | 'uploading' | 'processing' | 'uploaded' | 'error'
  fileId?: string
  attachmentId?: string
  attributeGroupId?: string
  documentId?: string
  cancelUpload?: () => void
}

export interface UploaderProps {
  children?: React.ReactNode
  onFileUploaded?: (args: UploadingFile) => void | Promise<void>
  onAllFilesUploaded?: (files: LocalFile[]) => void | Promise<void>
  onFileRemoved?: (file: UploadingFile) => void | Promise<void>
}

export const Uploader: React.FC<UploaderProps> = ({
  onFileUploaded,
  onFileRemoved,
  children,
}) => {
  const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([])

  const updateUploadingFile = (
    fingerprint: string,
    updatedFile: Partial<UploadingFile>
  ) => {
    setUploadingFiles((prev) =>
      prev.map((uf) =>
        uf.fingerprint === fingerprint ? { ...uf, ...updatedFile } : uf
      )
    )
  }

  const handleFileSelection = (value: LocalFile[] | null) => {
    const newFiles = uniqBy(value || [], getFileFingerprint)
    const newFingerprints = newFiles.map(getFileFingerprint)
    const existingFingerprints = uploadingFiles.map((uf) => uf.fingerprint)

    const removedFingerprints = existingFingerprints.filter(
      (fingerprint) => !newFingerprints.includes(fingerprint)
    )

    removedFingerprints.forEach((fingerprint) => {
      const uf = uploadingFiles.find((uf) => uf.fingerprint === fingerprint)
      uf?.cancelUpload?.()
    })

    setUploadingFiles((prev) =>
      prev.filter((uf) => !removedFingerprints.includes(uf.fingerprint))
    )

    const addedFiles = newFiles.filter(
      (file) => !existingFingerprints.includes(getFileFingerprint(file))
    )

    addedFiles.forEach((file) => {
      startUpload(file)
    })
  }

  const startUpload = async (file: LocalFile) => {
    const fingerprint = getFileFingerprint(file)

    const response = await apiClient.uploads.signature
      .$get({ query: { name: file.name, mimeType: file.type } })
      .then((r) => formatClientResponse(r))

    const { promise, cancelUpload } = uploadFileWithProgress({
      file,
      signedUrl: response.url,
      onUploaded: async () => {
        await apiClient.files[':fileId'].uploaded.$put({
          param: { fileId: response.fileId },
        })

        // mark as uploaded in local state
        updateUploadingFile(fingerprint, { status: 'uploaded', progress: 100 })

        // inform parent that the file has been uploaded and wait for it
        try {
          await onFileUploaded?.({
            file,
            fingerprint,
            progress: 100,
            status: 'uploaded',
            fileId: response.fileId,
            cancelUpload,
          })
        } finally {
          // clear from uploader once parent finished handling (e.g., created attachment and refreshed list)
          setUploadingFiles((prev) =>
            prev.filter((uf) => uf.fingerprint !== fingerprint)
          )
        }
      },
      onProgress: (progressEvent) => {
        updateUploadingFile(fingerprint, {
          status: 'uploading',
          progress: progressEvent.percentage,
        })
      },
    })

    setUploadingFiles((prev) => [
      ...prev,
      {
        file,
        fingerprint,
        progress: 0,
        status: 'idle',
        fileId: response.fileId,
        cancelUpload,
      },
    ])

    return promise
  }
  return (
    <>
      <FileUploader
        dropzoneOptions={dropZoneConfig}
        value={uploadingFiles.map((uf) => uf.file)}
        onValueChange={handleFileSelection}
      >
        <div className="relative rounded-lg border border-dashed p-2 shadow-sm">
          <FileInput className="outline-dashed outline-1 outline-white">
            {children ?? (
              <div className="flex flex-col items-center justify-center gap-2 py-4">
                <CloudUploadIcon className="size-8 text-gray-500" />
                <p className="text-center text-sm leading-normal text-gray-500 dark:text-gray-400">
                  Clicca qui o trascina i file per l'upload
                </p>
              </div>
            )}
          </FileInput>
        </div>

        <FileUploaderContent className="-mx-1 mb-2">
          {uploadingFiles.map((uf, i) => (
            <UploaderItem
              key={uf.fingerprint}
              index={i}
              uploadingFile={uf}
              onChange={(file) => updateUploadingFile(file.fingerprint, file)}
              onRemove={onFileRemoved}
            />
          ))}
        </FileUploaderContent>
      </FileUploader>
    </>
  )
}

function getFileFingerprint(file: LocalFile): string {
  return compact([
    file.name,
    file.size,
    file.type,
    file.lastModified,
    file.webkitRelativePath,
  ]).join('-')
}
